import { useCallback } from 'react';
import { BarChart3, List, FileText } from 'lucide-react';

import {
  BaseAnalyticsFilter,
  SessionDetailFilter,
} from '@repo/shared/dtos/analytics/AnalyticsFilterDto';
import { SessionTrendDataDto } from '@repo/shared/dtos/analytics/AnalyticsResponseDto';

import { useSessionTrends, useSessionLog } from '../../../hooks/analytics/useAnalytics';
import { useWeeklySessionExportData } from '../../../utils/export/useSessionExportData';

import { BaseTrendCard } from '../shared';
import { ControlOption } from '../shared/types';
import { ANALYTICS_CONSTANTS } from '../constants';
import { SessionTrendsChart } from './SessionTrendsChart';
import { SessionTrendsModal } from './SessionTrendsModal';
import { SessionLogPanel } from './SessionTrendsLogPanel';

interface SessionTrendsProps {
  filters: BaseAnalyticsFilter;
}

export function SessionTrends({ filters }: SessionTrendsProps) {
  const derivedGranularity: 'month' | 'day' = (() => {
    if (filters.granularity) return filters.granularity;
    if (filters.startDate && filters.endDate) {
      const diffMs = Math.abs(filters.endDate.getTime() - filters.startDate.getTime());
      const diffDays = diffMs / (1000 * 60 * 60 * 24);
      return diffDays <= ANALYTICS_CONSTANTS.MAX_DAILY_RANGE_DAYS ? 'day' : 'month';
    }
    return 'month';
  })();

  const dataHook = (f: BaseAnalyticsFilter) => useSessionTrends(f);

  const detailFilters: SessionDetailFilter = {
    startDate: filters.startDate,
    endDate: filters.endDate,
    experienceIds: filters.experienceIds,
    experienceTitle: filters.experienceTitle,
    unitIds: filters.unitIds,
  };

  const hasDateRange = Boolean(detailFilters.startDate && detailFilters.endDate);

  const { data: trendData } = dataHook(filters);
  const {
    data: sessionLogData,
    isLoading: isLogLoading,
    error: logError,
    refetch: refetchLog,
    status: logStatus,
  } = useSessionLog(detailFilters, {
    enabled: hasDateRange,
  });

  const logExportData = useWeeklySessionExportData(
    sessionLogData ?? [],
    detailFilters.startDate,
    detailFilters.endDate,
  );

  const viewOptions: ControlOption<'chart' | 'table' | 'log'>[] = [
    { value: 'chart', label: 'Chart', icon: <BarChart3 className="h-4 w-4" /> },
    { value: 'table', label: 'Table', icon: <List className="h-4 w-4" /> },
    { value: 'log', label: 'Log', icon: <FileText className="h-4 w-4" /> },
  ];

  const dropdownViewOptions: Array<'chart' | 'table'> = ['chart', 'table'];

  const getExportData = useCallback(
    (activeView: 'chart' | 'table' | 'log') => {
      if (activeView === 'log') {
        return logExportData;
      }
      return trendData;
    },
    [logExportData, trendData],
  );

  const getExportFileName = useCallback((activeView: 'chart' | 'table' | 'log') => {
    if (activeView === 'log') {
      return 'session-log-weekly';
    }
    return 'session-trends';
  }, []);

  const resolveTitle = (metric: 'sessions' | 'minutes') =>
    metric === 'minutes' ? 'Minutes Trends' : 'Session Trends';

  return (
    <BaseTrendCard
      title={resolveTitle}
      filters={filters}
      dataHook={dataHook}
      getDate={(trend: SessionTrendDataDto) => trend.monthStart}
      getMainMetric={(trend: SessionTrendDataDto) => trend.sessionCount}
      getSecondaryMetric={(trend: SessionTrendDataDto) => trend.totalMinutes}
      getChange={(trend: SessionTrendDataDto) => trend.percentageChange}
      dateFormat={derivedGranularity === 'day' ? 'date' : 'month'}
      emptyMessage="No trend data available"
      ChartComponent={SessionTrendsChart}
      viewOptions={viewOptions}
      dropdownViewOptions={dropdownViewOptions}
      renderLog={() => (
        <SessionLogPanel
          data={sessionLogData}
          isLoading={isLogLoading}
          error={logError}
          onRetry={refetchLog}
          status={logStatus}
        />
      )}
      exportData={getExportData}
      exportFileName={getExportFileName}
      DetailModal={SessionTrendsModal}
    />
  );
}
